import { useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import getListOrTableField from '@helpers/getListOrTableField';
import trackEvents from '@/helpers/trackEvents';

import formValuesToResponses from '../../helpers/formValuesToResponses';

type Props = {
  formData: {
    sections: Array<
      & Parameters<typeof formValuesToResponses>[5][number]
    >
  }
  formValues: Record<string, unknown>;
};

const HandleAutoSave: React.FC<Props> = ({ formData, formValues }) => {
  trackEvents.track('HandleAutoSave- render');
  const {
    fieldsById,
    onSubmit,
  } = useQuestionnaireContext();
  const {
    formMethods,
    getResponseSource,
    getAiRequestId,
    getAutofillStatus,
    getExplanationHtml,
  } = useQuestionnaireFormContext();

  const previousValuesRef = useRef<Record<string, unknown>>({});
  const functionsRef = useRef({
    formMethods,
    getAiRequestId,
    getAutofillStatus,
    getExplanationHtml,
    getResponseSource,
    onSubmit,
  });
  functionsRef.current = {
    formMethods,
    getAiRequestId,
    getAutofillStatus,
    getExplanationHtml,
    getResponseSource,
    onSubmit,
  };

  const debouncedSaveDraft = useCallback(
    debounce(({
      changedValues,
      currentValues,
      dirtyFieldKeys,
      fieldsById: fieldsByIdInternal,
      sections,
    }: {
      changedValues: Record<string, unknown>;
      currentValues: Record<string, unknown>;
      dirtyFieldKeys: string[];
      fieldsById: Parameters<typeof getListOrTableField>[0]['fieldsById'];
      sections: Parameters<typeof formValuesToResponses>[5];
    }) => {
      trackEvents.track('HandleAutoSave- called debouncedSaveDraft');
      const data = formValuesToResponses(
        changedValues,
        functionsRef.current.getResponseSource,
        functionsRef.current.getAiRequestId,
        functionsRef.current.getAutofillStatus,
        functionsRef.current.getExplanationHtml,
        sections,
        (fieldId: string) => Boolean(getListOrTableField({
          fieldId,
          fieldsById: fieldsByIdInternal,
        })),
      );
      previousValuesRef.current = currentValues;
      // @todo - handle failed save.
      // could enough to put back the old previousValuesRef, but need to think about that more.
      // especially around a second autosave while 1 is in flight and the first fails, but the second passes?
      functionsRef.current.onSubmit(data);
      dirtyFieldKeys.forEach(key => functionsRef.current.formMethods.resetField(key, { defaultValue: currentValues[key] }));
    }, 2000),
    [],
  );

  useEffect(() => {
    // @todo - better handling of complext fields.
    //   eg if address has {zip: false, city: false, line1: false, line2: false, state: false}
    //   then there are no changes.
    // @todo detect if lists have changes. currently save the whole list if one item is changed.
    const dirtyFieldKeys = Object.keys(formMethods.formState.dirtyFields);
    const changedValues: Record<string, unknown> = {};
    let hasChanges = false;
    Object.keys(formValues)
      .filter((fieldKey) => dirtyFieldKeys.includes(fieldKey))
      .forEach((key) => {
        hasChanges = true;
        changedValues[key] = formValues[key];
      });

    if (hasChanges) {
      trackEvents.track('HandleAutoSave- checked for changes');
      debouncedSaveDraft({
        changedValues,
        currentValues: formValues,
        dirtyFieldKeys,
        fieldsById,
        sections: formData.sections,
      });
    }
  }, [
    formValues,
    formData.sections,
    fieldsById,
    debouncedSaveDraft,
    formMethods,
  ]);

  return null;
};

export default HandleAutoSave;
